import Box from '@mui/material/Box'
import Divider from '@mui/material/Divider'
import Skeleton from '@mui/material/Skeleton'
import type { SxProps, Theme } from '@mui/material/styles'
import React from 'react'

import { KestrixDataTableSkeleton } from '../KestrixDataTable/KestrixDataTableSkeleton.tsx'
import { borderRadius, colors } from '../KestrixDesignSystem.jsx'
import colorTokens from '../tokens/colors.ts'
import { chartCardShellLayoutSx } from './styles/kestrix-chart-card-shell.ts'

const n = colorTokens.global.neutral

export const CHART_CARD_SKELETON_VARIANTS = ['bar', 'pie', 'table'] as const
export type ChartCardSkeletonVariant =
  (typeof CHART_CARD_SKELETON_VARIANTS)[number]

const titleSkeletonSx: SxProps<Theme> = {
  bgcolor: colors.neutral[200],
  borderRadius: borderRadius.sm
}

const subtleSkeletonSx: SxProps<Theme> = {
  bgcolor: colors.neutral[100],
  borderRadius: borderRadius.sm
}

const BAR_HEIGHTS_PX = [64, 102, 80, 126, 52, 88, 70] as const

const BarChartSkeletonContent = (): React.ReactElement => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'flex-end',
      justifyContent: 'space-between',
      gap: '6px',
      width: '100%',
      minHeight: 136,
      flex: 1,
      px: 0.5,
      pt: 2
    }}
  >
    {BAR_HEIGHTS_PX.map((heightPx, i) => (
      <Skeleton
        key={i}
        variant="rounded"
        height={heightPx}
        sx={{
          flex: '1 1 0',
          minWidth: 0,
          bgcolor: n[200],
          borderRadius: borderRadius.sm
        }}
      />
    ))}
  </Box>
)

const PieChartSkeletonContent = (): React.ReactElement => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '100%',
      minHeight: 160,
      flex: 1,
      py: 1
    }}
  >
    <Box sx={{ position: 'relative', width: 140, height: 140 }}>
      <Skeleton
        variant="circular"
        width={140}
        height={140}
        sx={{ bgcolor: n[200] }}
      />
      <Box
        sx={{
          position: 'absolute',
          left: '50%',
          top: '50%',
          transform: 'translate(-50%, -50%)',
          width: 56,
          height: 56,
          borderRadius: '50%',
          bgcolor: n[500],
          border: `2px solid ${n[500]}`,
          boxSizing: 'border-box'
        }}
      />
    </Box>
  </Box>
)

/**
 * Per-variant config for the table skeleton. `columnCount` sizes the inner
 * {@link KestrixDataTableSkeleton}; `tabCount` controls how many tab
 * placeholders are rendered to the left of the search-input placeholder
 * (set to 0 for tables without tabs). `showDescription` toggles the
 * description-bar placeholder under the title.
 */
export type ChartCardSkeletonTableConfig = {
  readonly columnCount?: number
  readonly tabCount?: number
  readonly rowCount?: number
  readonly showDescription?: boolean
}

type TableSkeletonContentProps = Pick<
  ChartCardSkeletonTableConfig,
  'columnCount' | 'tabCount' | 'rowCount'
>

const TableSkeletonContent = ({
  columnCount = 3,
  tabCount = 0,
  rowCount = 4
}: TableSkeletonContentProps): React.ReactElement => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      gap: 2,
      width: '100%',
      flex: 1,
      minWidth: 0
    }}
  >
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 1
      }}
    >
      <Box sx={{ display: 'flex', gap: 0.75 }}>
        {Array.from({ length: tabCount }).map((_, index) => (
          <Skeleton
            key={`tab-${index}`}
            variant="rounded"
            width={88}
            height={28}
            sx={subtleSkeletonSx}
          />
        ))}
      </Box>
      <Skeleton
        variant="rounded"
        width={240}
        height={28}
        sx={subtleSkeletonSx}
      />
    </Box>

    <KestrixDataTableSkeleton
      showTitle={false}
      columnCount={columnCount}
      rowCount={rowCount}
      border={false}
      padding={false}
      rounded={false}
      background="primary"
    />
  </Box>
)

type SkeletonContentProps = {
  readonly variant: ChartCardSkeletonVariant
  readonly tableConfig?: ChartCardSkeletonTableConfig
}

/** Inner chart-area skeleton (bars, pie, or table). */
export const KestrixChartCardSkeletonContent = ({
  variant,
  tableConfig
}: SkeletonContentProps): React.ReactElement => {
  if (variant === 'pie') return <PieChartSkeletonContent />
  if (variant === 'table')
    return (
      <TableSkeletonContent
        columnCount={tableConfig?.columnCount}
        tabCount={tableConfig?.tabCount}
        rowCount={tableConfig?.rowCount}
      />
    )
  return <BarChartSkeletonContent />
}

/**
 * Full loading body matching {@link KestrixMetricCard} skeleton structure.
 *
 * For `bar` / `pie` variants this renders a title row, chart placeholder, and
 * footer row. For the `table` variant it mirrors the real chart-card chrome
 * more closely — title + optional description + divider, then the column- and
 * tab-aware table skeleton — to avoid layout shift when the data resolves.
 */
export const KestrixChartCardSkeletonRows = ({
  variant,
  tableConfig
}: SkeletonContentProps): React.ReactElement => {
  if (variant === 'table') {
    const showDescription = tableConfig?.showDescription ?? true
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '8px',
          width: '100%',
          minHeight: 0,
          flex: 1
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '6px',
            width: '100%'
          }}
        >
          <Skeleton
            variant="rounded"
            width={220}
            height={16}
            sx={titleSkeletonSx}
          />
          {showDescription && (
            <Skeleton
              variant="rounded"
              width="60%"
              height={14}
              sx={subtleSkeletonSx}
            />
          )}
          <Divider sx={{ margin: '12px 0' }} />
        </Box>

        <TableSkeletonContent
          columnCount={tableConfig?.columnCount}
          tabCount={tableConfig?.tabCount}
          rowCount={tableConfig?.rowCount}
        />
      </Box>
    )
  }

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          width: '100%',
          justifyContent: 'space-between'
        }}
      >
        <Skeleton
          variant="rounded"
          width={166}
          height={20}
          sx={{ bgcolor: n[100], borderRadius: borderRadius.sm }}
        />
      </Box>
      <KestrixChartCardSkeletonContent variant={variant} />
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          width: '100%',
          justifyContent: 'space-between'
        }}
      >
        <Skeleton
          variant="rounded"
          width={75}
          height={16}
          sx={{ bgcolor: n[100], borderRadius: borderRadius.sm }}
        />
        <Skeleton
          variant="circular"
          width={16}
          height={16}
          sx={{ bgcolor: n[100] }}
        />
      </Box>
    </>
  )
}

export type KestrixChartCardSkeletonProps = {
  readonly variant?: ChartCardSkeletonVariant
  readonly tableConfig?: ChartCardSkeletonTableConfig
  readonly sx?: SxProps<Theme>
} & Omit<React.ComponentPropsWithoutRef<typeof Box>, 'sx'>

/** Standalone chart card loading placeholder with full KDS loading chrome. */
export const KestrixChartCardSkeleton = ({
  variant = 'bar',
  tableConfig,
  sx,
  ...boxProps
}: KestrixChartCardSkeletonProps): React.ReactElement => (
  <Box
    className="kestrix-chart-card-root"
    sx={{
      ...chartCardShellLayoutSx,
      backgroundColor: n[500],
      borderColor: n[100],
      ...sx
    }}
    {...boxProps}
  >
    <KestrixChartCardSkeletonRows variant={variant} tableConfig={tableConfig} />
  </Box>
)

export default KestrixChartCardSkeleton
